"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase_client";

type ProfileSummary = {
  username?: string | null;
  connections_count?: number | null;
  creator_role?: string | null;
  creator_badge?: string | null;
  is_collaborator?: boolean | null;
};

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profile: ProfileSummary | null;
  profileLoading: boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthContextValue>({
    user: null,
    session: null,
    loading: true,
    profile: null,
    profileLoading: false,
  });

  useEffect(() => {
    let cancelled = false;

    const applySessionState = (
      session: Session | null,
      opts?: { allowClearToAnon?: boolean }
    ) => {
      setState((prev) => {
        // iOS Safari can occasionally return a transient null session on route transitions /
        // tab restores even when the user is still logged in via cookies.
        // Avoid flipping the UI to anonymous unless we have a clear sign-out signal.
        if (!session && prev.user && !opts?.allowClearToAnon) {
          return { ...prev, loading: false };
        }

        const nextUser = session?.user ?? null;
        const nextUserId = nextUser?.id ?? null;
        const prevUserId = prev.user?.id ?? null;
        const userChanged = nextUserId !== prevUserId;

        let cachedProfile: ProfileSummary | null = null;
        let cachedHasUsername = false;
        if (userChanged && nextUserId) {
          try {
            const raw = localStorage.getItem(`ta_profile_summary_${nextUserId}`);
            if (raw) {
              const parsed = JSON.parse(raw) as any;
              cachedProfile = {
                username: parsed?.username ?? null,
                connections_count: parsed?.connections_count ?? null,
                creator_role: parsed?.creator_role ?? null,
                creator_badge: parsed?.creator_badge ?? null,
                is_collaborator: parsed?.is_collaborator ?? null,
              };
              cachedHasUsername = Boolean(cachedProfile?.username);
            }
          } catch {
            cachedProfile = null;
            cachedHasUsername = false;
          }
        }

        return {
          user: nextUser,
          session,
          loading: false,
          // Only reset profile when the user changes (sign-in/out), not on token refresh.
          profile: userChanged ? cachedProfile : prev.profile,
          profileLoading: nextUser ? (userChanged ? !cachedHasUsername : prev.profileLoading) : false,
        };
      });
    };

    const syncFromSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error) {
          // Treat session read errors as transient (not a sign-out), especially on iOS Safari.
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }
        applySessionState(data.session ?? null, { allowClearToAnon: false });
      } catch {
        if (cancelled) return;
        // Same as above: keep the last known auth state instead of hard-resetting to anonymous.
        setState((prev) => ({ ...prev, loading: false }));
      }
    };

    syncFromSession();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      applySessionState(session ?? null, {
        allowClearToAnon: event === "SIGNED_OUT",
      });
    });

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        syncFromSession();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  // Hydrate basic profile summary once per authenticated user, and keep it stable across route changes.
  useEffect(() => {
    const userId = state.user?.id ?? null;
    if (!userId) return;

    let cancelled = false;

    const loadProfileSummary = async (uid: string) => {
      try {
        const [{ data: profileData }, { data: connRow }] = await Promise.all([
          supabase
            .schema("api")
            .from("profiles")
            .select("username, creator_role, creator_badge, is_collaborator")
            .eq("id", uid)
            .maybeSingle(),
          supabase
            .schema("api")
            .from("connection_counts")
            .select("connections_count")
            .eq("user_id", uid)
            .maybeSingle(),
        ]);

        if (cancelled) return;
        const summary: ProfileSummary = {
          username: (profileData as any)?.username ?? null,
          connections_count: (connRow as any)?.connections_count ?? 0,
          creator_role: (profileData as any)?.creator_role ?? null,
          creator_badge: (profileData as any)?.creator_badge ?? null,
          is_collaborator: (profileData as any)?.is_collaborator ?? null,
        };
        try {
          localStorage.setItem(`ta_profile_summary_${uid}`, JSON.stringify(summary));
        } catch {}
        setState((prev) => {
          if (prev.user?.id !== uid) return prev;
          return {
            ...prev,
            profile: summary,
            profileLoading: false,
          };
        });
      } catch {
        if (cancelled) return;
        setState((prev) => {
          if (prev.user?.id !== uid) return prev;
          return {
            ...prev,
            profile: prev.profile ?? { username: null, connections_count: 0 },
            profileLoading: false,
          };
        });
      }
    };

    loadProfileSummary(userId);

    // Allow other parts of the app (e.g. /account/setup) to signal "profile just changed"
    // so the top bar does not require a manual refresh (notably on iOS Safari).
    const onProfileUpdated = (ev: Event) => {
      const anyEv = ev as any;
      const uid = anyEv?.detail?.userId as string | undefined;
      if (!uid) return;
      if (uid !== userId) return;
      loadProfileSummary(uid);
    };
    window.addEventListener("ta_profile_updated", onProfileUpdated as EventListener);

    return () => {
      cancelled = true;
      window.removeEventListener("ta_profile_updated", onProfileUpdated as EventListener);
    };
  }, [state.user?.id]);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    return { user: null, session: null, loading: true, profile: null, profileLoading: false };
  }
  return ctx;
}
